import styles from './CheckInForm.module.scss';
import Button from 'components/Button/Button';
import React, { useState } from 'react';
import SelectComponent from 'components/Input/Select';
import { checkInTypeOptions } from '../../variables';
import DateTimeSelect from 'components/Input/DateTimeSelect';
import { store } from 'stores/Store';
import useExternalApiService from 'helpers/ExternalApiService';
import { ReactComponent as CameraIcon } from "assets/camera.svg";
import { Image, Modal } from '@mantine/core';
import { Carousel } from '@mantine/carousel';
import '@mantine/carousel/styles.css';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import Toastr from 'components/Toastr';

import { logEvent } from '@amplitude/analytics-browser'; // ← browser SDK



interface ICheckInForm {
  navigateBack: () => void;
  selectedAddress: any;
  onCheckinAdded?: () => void;
}

const CheckInForm = ({ navigateBack, selectedAddress, onCheckinAdded }: ICheckInForm) => {
  const [selectedDateTime, setSelectedDateTime] = React.useState<string>(() => {
    const now = new Date();
    // Get the raw time without timezone adjustments
    return new Date(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      now.getUTCHours(),
      now.getUTCMinutes()
    ).toISOString();
  });
  const [notes, setNotes] = React.useState('');
  const [practitioners, setPractitioners] = React.useState<any[]>([]);

  // const [selectedPractitioner, setSelectedPractitioner] = React.useState<any>(null);
  const [selectedCheckInType, setSelectedCheckInType] = React.useState<any>(checkInTypeOptions[0]);
  const [selectedFollowUpDate, setSelectedFollowUpDate] = React.useState<string>(new Date().toISOString());
  const apiUrl = process.env.REACT_APP_EAI_URL;
  const [isAddingCheckin, setIsAddingCheckin] = useState(false);
  const { postData } = useExternalApiService(apiUrl);

  // Verification-related state
  const [isVerified, setIsVerified] = React.useState<boolean | null>(null);
  const [isCheckingLocation, setIsCheckingLocation] = React.useState(true);

  // Photo-related state
  const [showPhotoModal, setShowPhotoModal] = React.useState(false);
  const [showCamera, setShowCamera] = React.useState(false);
  const [capturedImage, setCapturedImage] = React.useState<string | null>(null);
  const [showCarouselModal, setShowCarouselModal] = React.useState(false);
  const [carouselIndex, setCarouselIndex] = React.useState(0);
  const [photos, setPhotos] = React.useState<{id: number | string, url: string, api_url?: string, imgObject?: HTMLImageElement, dataUrl?: string}[]>([]);
  const [showToast, setShowToast] = React.useState(false);
  const [toastMessage, setToastMessage] = React.useState('');

  // Refs for photo functionality
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);

  // Amplitude: helper to track all button click submission to AI
  const trackClick = (buttonName: string) => {
    const label = buttonName
    logEvent(label, { buttonName });
  };

  // Verification handling function
  const checkVerificationStatus = async () => {
    setIsCheckingLocation(true);

    if ("geolocation" in navigator) {
      try {
        const distance = await new Promise<number>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition((position) => {
            const lat1 = position.coords.latitude;
            const lon1 = position.coords.longitude;
            const lat2 = selectedAddress?.latitude;
            const lon2 = selectedAddress?.longitude;

            // Calculate distance using Haversine formula
            const R = 6371; // Radius of earth in km
            const dLat = (lat2 - lat1) * Math.PI / 180;
            const dLon = (lon2 - lon1) * Math.PI / 180;

            const a =
              Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
            const distance = R * c;

            resolve(Math.round(distance * 0.621371)); // Convert to miles and round
          }, (error) => {
            reject(error);
          });
        });

        const verified = distance <= 5;
        setIsVerified(verified);
      } catch (error) {
        console.error('Error getting location:', error);
        setIsVerified(false);
      }
    } else {
      setIsVerified(false);
    }

    setIsCheckingLocation(false);
  };

  // Photo handling functions
  const handleShowToast = (message: string) => {
    setShowToast(true);
    setToastMessage(message);
  };

  const handlePhotoUpload = () => {
    setShowPhotoModal(true);
  };

  const handlePhotoClick = (index: number) => {
    if (photos.length > 0) {
      setCarouselIndex(index);
      setShowCarouselModal(true);
    }
  };

  const handleGallerySelect = () => {
    setShowPhotoModal(false);
    setShowCamera(false);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleCameraSelect = async () => {
    setShowPhotoModal(false);
    setCapturedImage(null);
    setShowCamera(true);

    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      try {
        const constraints = {
          video: {
            facingMode: 'environment',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        };
        
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error("Cannot access camera, fallback to file input", err);
        handleShowToast('Camera access denied. Using gallery instead.');
        setShowCamera(false);
        if (fileInputRef.current) {
          fileInputRef.current.click();
        }
      }
    } else {
      console.warn("MediaDevices not supported, fallback to file input");
      handleShowToast('Camera not supported. Using gallery instead.');
      setShowCamera(false);
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }
  };

  const takePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    
    if (ctx) {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageUrl = canvas.toDataURL("image/png");
      
      const stream = video.srcObject as MediaStream;
      stream?.getTracks().forEach((track) => track.stop());
      setShowCamera(false);

      await addPhotoToList(imageUrl);
    }
  };

  const uploadImageToAPI = async (imageUrl: string, checkinId: number) => {
    try {
      const res = await fetch(imageUrl);
      const blob = await res.blob();
  
      const sasResponse = await postData('/api/get_azure_sas', {
        fmc_id: store.user?.userName || '',
        practitioner_id: store.currentPractitioner.id,
        checkin_id: checkinId,
        filename: `checkin_${Date.now()}.jpg`
      });
  
      const { blob_url } = sasResponse;
  
      const headers = new Headers();
      headers.append('x-ms-blob-type', 'BlockBlob');
  
      const uploadResponse = await fetch(blob_url, {
        method: 'PUT',
        headers,
        body: blob
      });
  
      if (!uploadResponse.ok) {
        throw new Error(`Azure upload failed with status ${uploadResponse.status}`);
      }

      return { success: true, blob_url };
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };
  
  const addPhotoToList = (imageUrl: string) => {
    if (photos.length >= 10) {
      handleShowToast('Maximum of 10 photos allowed');
      return;
    }
    
    const newPhoto = {
      id: photos.length + 1,
      url: imageUrl, 
      dataUrl: imageUrl 
    };
    setPhotos(prev => [...prev, newPhoto]);
    setCapturedImage(null);
    
    handleShowToast('Photo added successfully');
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      await addPhotoToList(url);
    }
    e.target.value = '';
  };

  const handleDeletePhoto = () => {
    const remainingPhotos = photos.filter((_, i) => i !== carouselIndex);
    setPhotos(remainingPhotos);
    
    if (remainingPhotos.length === 0) {
      setShowCarouselModal(false);
    } else {
      if (carouselIndex >= remainingPhotos.length) {
        setCarouselIndex(remainingPhotos.length - 1);
      }
    }
    
    handleShowToast('Photo removed successfully');
  };

  const handleSave = async () => {
    setIsAddingCheckin(true);

    // Use the already-calculated verification status
    const verified = isVerified ?? false;

    trackClick('Successfully Submitted Checkin');

    try {
      // First create the check-in
      const checkInResult = await postData('/api/add_checkin', {
        "practitioner_id": store.currentPractitioner.id,
        "checkin_data": {
          "type": selectedCheckInType.value,
          "notes": notes,
          "follow_up_date": selectedFollowUpDate,
          "verified": verified,
          "checkin_address": selectedAddress?.id,
        }
      });

      // If we have photos and a check-in was created successfully, upload them
      if (photos.length > 0 && checkInResult?.checkin_id) {
        try {
          // Upload photos one by one
          for (const photo of photos) {
            await uploadImageToAPI(photo.url, checkInResult.checkin_id);
          }
          handleShowToast('Check-in and photos saved successfully');
        } catch (photoError) {
          console.error('Error uploading photos:', photoError);
          handleShowToast('Check-in saved but some photos may not have uploaded');
        }
      } else if (photos.length === 0) {
        handleShowToast('Check-in saved successfully');
      }

      onCheckinAdded?.();
      navigateBack();
    } catch (err) {
      console.error(err);
      handleShowToast('Error saving check-in');
    } finally {
      setIsAddingCheckin(false);
    }
  }

  React.useEffect(() => {
    if (store.checkIn) {
      setNotes(store.checkIn.notes ?? '');
    }
    // getPractitioners();
  }, []);

  // Check verification status when component mounts or address changes
  React.useEffect(() => {
    checkVerificationStatus();
  }, [selectedAddress]);

  // const getPractitioners = async () => {
  //   try {
  //     const result = await postData('/api/get_practitioners_for_chat', {"fmc_email": store.impersonatedUser});
  //     const formattedPractitioners = result.practitioners.map((pract: any) => ({
  //       label: pract.primary_name,
  //       value: pract.id,
  //     }));
      
  //     setPractitioners(formattedPractitioners);    
  //     // Only look for the practitioner after we have the data
  //     const practitioner = result.practitioners.find((p: any) => {
  //       return p.id === store.currentPractitioner?.id;
  //     });
  //     if (practitioner) {
  //       setSelectedPractitioner({
  //         label: practitioner.primary_name,
  //         value: practitioner.id,
  //       });
  //     }
  //   } catch (error) {
  //     console.error('Error fetching practitioners:', error);
  //   }
  // };

  return(
    <div className={styles.wrapper}>
      <div className={styles.headerWrapper}>
        <p className={styles.headerText}>Check-In for: </p>
        <div className={styles.btnsWrapper}>
          <Button
            color="#00C542"
            textColor="white"
            borderColor="#00C54280"
            type="outline"
            onClick={() => {
              if (!isAddingCheckin) {
                handleSave();
              }
            }}
          >
            SAVE
          </Button>
          <Button type="outline" onClick={navigateBack}>CANCEL</Button>
        </div>
      </div>

      <div className={styles["current-pract"]}>{store.currentPractitioner.primary_name}</div>

      {/* Verification Status Banner */}
      {!isCheckingLocation && isVerified !== null && (
        <div>
          {isVerified ? (
            <div className={styles.verifiedWrapper}>
              <p className={styles.verifiedText}>Verified Check-In</p>
            </div>
          ) : (
            <div className={styles.unverifiedWrapper}>
              <p className={styles.unverifiedText}>Unverified Check-In</p>
            </div>
          )}
        </div>
      )}
      {/* <SelectComponent
        value={{label: "Mona Solar", value: "Mona Solar"}}
        onChange={() => {}}
        options={[{label: "Mona Solar", value: "Mona Solar"}]} 
        placeholder="Select a Practitioner" 
      /> */}
      <p className={styles.label}>Time</p>
      <DateTimeSelect
        value={selectedDateTime}
        setValue={(e: string) => {
          setSelectedDateTime(e);
          setSelectedFollowUpDate(e);
        }}
      />
      <div className={styles.divider}></div>
      <p className={styles.label}>Log Type</p>
      <SelectComponent options={checkInTypeOptions} placeholder="Select a Type" value={selectedCheckInType} onChange={(e: any) => setSelectedCheckInType(e)} />
      <p className={styles.label}>Notes</p>
      <textarea
        className={styles.textarea}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Enter your notes here..."
      />

      <div className={styles.divider}></div>

      <div className={styles.headerRow}>
        <p className={styles.label}>Photos</p>
        <div className={styles.label}>You can upload up to 10 photos</div>
      </div>

      <div className={styles.photoUploadSection}>
        <div className={styles.photosGrid}>
          {photos.length < 10 && (
            <div className={styles.photoUploadCard} onClick={handlePhotoUpload}>
              <div className={styles.cameraIcon}>
                <CameraIcon />
              </div>
              <div className={styles.uploadText}>Add Check-In Photo</div>
            </div>
          )}
          
          {photos.map((photo, index) => (
            <div key={photo.id} className={styles.photoCard} onClick={() => handlePhotoClick(index)}>
              <img 
                src={photo.dataUrl || photo.url} 
                alt={`Photo ${photo.id}`} 
                className={styles.photoImage}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const placeholder = target.nextElementSibling as HTMLElement;
                  if (placeholder) placeholder.style.display = 'flex';
                }}
              />
              <div className={styles.photoPlaceholder} style={{display: 'none'}}>
                <span className={styles.photoText}>Photo {photo.id}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Toastr 
        backgroundColor="white" 
        message={toastMessage} 
        isVisible={showToast} 
        onClose={() => setShowToast(false)} 
      />

      {/* Full-screen loading overlay */}
      {isAddingCheckin && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingContent}>
            <div className={styles.loadingSpinner}></div>
            <div className={styles.loadingText}>Saving check-in...</div>
          </div>
        </div>
      )}

      {/* Hidden file input */}
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {/* Camera Preview */}
      {showCamera && (
        <div className={styles.cameraPreview}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className={styles.cameraVideo}
          />
          <button className={styles.captureButton} onClick={takePhoto}>
            <CameraIcon width={40} height={40} />
          </button>
        </div>
      )}

      {/* Canvas for capturing photo */}
      <canvas ref={canvasRef} style={{ display: "none" }} />

      <ConfirmationModal
        isOpen={showPhotoModal}
        onClose={() => setShowPhotoModal(false)} 
        onConfirm={() => {setShowPhotoModal(false)}} 
        title="Photo Source"
        message=""
        cancelText="CANCEL"
        confirmText="DONE" 
        modalBodyStyle={{padding: '10px 0px 10px 0px'}}
        modalContentStyle={{maxWidth: '400px', width: '100%'}}
        modalHeaderStyle={{padding: 'margin: 0px'}}
        customRender={
          <div className={styles.photoOptions}>
            <div className={`${styles.photoOption} ${styles.galleryOption}`} onClick={handleGallerySelect}>
              Gallery
            </div>
            <div className={styles.photoDivider}></div>
            <div className={`${styles.photoOption} ${styles.cameraOption}`} onClick={handleCameraSelect}>
              Camera
            </div>
          </div>
        }
      />

      {/* Photo Carousel Modal */}
      <Modal
        opened={showCarouselModal}
        onClose={() => setShowCarouselModal(false)}
        size="lg"
        withCloseButton={true}
        styles={{
          body: {
            padding: '0',
          },
          content: {
            position: 'fixed',
            bottom: 0,
            top: 'auto',
            margin: 0,
            maxHeight: '100vh',
            maxWidth: '500px',
            height: 'auto',
            borderTopLeftRadius: '30px',
            borderTopRightRadius: '30px',
          },
          header: {padding: 15},
        }}
      >
        <div className={styles.carouselHeader}>
          Check-in Photos
        </div>
        <div className={styles.carouselWrapper}>
            <Carousel
              initialSlide={carouselIndex}
              withIndicators
              withControls={false}
              slideSize="100%"
              slideGap="md"
              onSlideChange={(index) => setCarouselIndex(index)}
              emblaOptions={{ 
                align: 'start', 
                slidesToScroll: 1,
                containScroll: 'trimSnaps',
                loop: false
              }}
              styles={{
                indicators: {
                  bottom: -120,
                },
                indicator: {
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255, 255, 255)',
                  border: 'none',
                  '&[data-active]': {
                    backgroundColor: '#000',
                  }
                }
              }}
            >
              {photos.map((photo, index) => (
                <Carousel.Slide key={photo.id}>
                  <img 
                    src={photo.dataUrl || photo.url} 
                    alt={`Photo ${photo.id}`} 
                    className={styles.carouselImage}
                    style={{
                      width: '100%',maxHeight: '350px',
                      objectFit: 'contain',
                      objectPosition: 'center'
                    }}
                  />
                </Carousel.Slide>
              ))}
            </Carousel>
        </div>
         <div className={styles.deletePhotoButton} onClick={handleDeletePhoto}>
           DELETE PHOTO
         </div>
      </Modal>
    </div>
  )
}

export default CheckInForm;
